import { InteractiveMapLocation } from "@redotech/redo-common-model-location-common/interactive-map-location";
import { useTranslation } from "@redotech/redo-i18n";
import MarkerPin from "@redotech/redo-icons/marker-pin-01.svg";
import { TrackableType } from "@redotech/redo-order-tracking-trackable-common/trackable";
import { Trackable } from "@redotech/redo-order-tracking-trackable-return-common/trackable";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { useQuery } from "@tanstack/react-query";
import { memo, useState } from "react";
import { InteractiveMap } from "./in-store-locations";
import * as returnLocationsStyles from "./return-locations-button.module.css";

const supportedCarrierLocations = [
  "USPS",
  "USPSReturns",
  "UspsShip",
  "FedEx",
  "FedExSmartPost",
];

export interface ReturnLocationsSearchParams {
  carrier: string;
  zip: string;
  country: string;
}

export const ReturnLocationsButton = memo(function ReturnLocationsButton({
  trackable,
  searchCarrierLocations,
}: {
  trackable: Trackable & { type: TrackableType.RETURN };
  searchCarrierLocations: (
    params: ReturnLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
}) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const initialZip = trackable?.shipping_address?.zip || "";
  const [zip, setZip] = useState(initialZip);
  const [searchedZip, setSearchedZip] = useState(initialZip);

  const carrier: string | undefined =
    trackable?.shipment?._shipment?.selected_rate?.carrier;

  const country = trackable.shipping_address?.country || "US";

  const query = useQuery({
    queryKey: ["carrierLocations", carrier, searchedZip, country],
    queryFn: () =>
      searchCarrierLocations({ carrier: carrier!, zip: searchedZip, country }),
    enabled: modalOpen && !!carrier && !!searchedZip,
    staleTime: Infinity,
  });

  if (!carrier || !supportedCarrierLocations.includes(carrier)) {
    return null;
  }

  return (
    <>
      <Flex pt="xs">
        <RedoButton
          hierarchy="secondary"
          IconLeading={MarkerPin}
          onClick={() => setModalOpen(true)}
          text={t("Drop off locations")}
        />
      </Flex>
      <RedoModal
        className={returnLocationsStyles.locationsModal}
        isOpen={modalOpen}
        onModalCloseRequested={() => setModalOpen(false)}
        title={t("Drop off locations")}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearchedZip(zip);
          }}
        >
          <Flex gap="sm" m="lg">
            <RedoTextInput
              fullWidth
              placeholder={t("Zip code")}
              setValue={setZip}
              value={zip}
            />
            <RedoButton
              hierarchy="secondary"
              text={t("Search")}
              type="submit"
            />
          </Flex>
        </form>
        {query.isPending && query.fetchStatus !== "idle" ? (
          <LoadingRedoAnimation />
        ) : query.error ? (
          <Text textColor="error">{t("Error loading locations")}</Text>
        ) : query.data ? (
          <InteractiveMap key={searchedZip} locations={query.data} />
        ) : null}
      </RedoModal>
    </>
  );
});
